(() => {
  if (customElements.get("slidershow-component")) return;

  class SliderShow extends HTMLElement {
    constructor() {
      super();
      this.track = null;
      this.slides = [];
      this.dots = null;
      this.fadeIndex = 0;
      this.autoplayTimer = null;
      this.autoplayInterval = 6000;
      this.autoplayEnabled = false;
      this.autoplayPaused = false;
      this.pauseOnHover = true;
      this.ignoreHoverPause = false;
      this.ignoreFocusPause = false;
      this.ignoreVisibility = false;
      this.prefersReducedMotion = null;
      this.hoverNav = null;
      this.cursor = null;
      this.cursorPrev = null;
      this.cursorNext = null;
      this.hoverZones = [];
      this.progress = null;
      this.progressCurrent = null;
      this.progressTotal = null;
      this.progressSegments = [];
      this.progressSubtitle = null;
      this.fadePointerId = null;
      this.fadeStartX = 0;
      this.fadeStartY = 0;
      this.fadeDeltaX = 0;
      this.fadeDeltaY = 0;
      this.onDotClick = this.onDotClick.bind(this);
      this.onPrevClick = this.onPrevClick.bind(this);
      this.onNextClick = this.onNextClick.bind(this);
      this.onZoneMove = this.onZoneMove.bind(this);
      this.onZoneClick = this.onZoneClick.bind(this);
      this.onHoverLeave = this.onHoverLeave.bind(this);
      this.onRestoreZones = this.onRestoreZones.bind(this);
      this.onFadePointerDown = this.onFadePointerDown.bind(this);
      this.onFadePointerMove = this.onFadePointerMove.bind(this);
      this.onFadePointerUp = this.onFadePointerUp.bind(this);
      this.onVisibilityChange = this.onVisibilityChange.bind(this);
      this.onMouseEnter = this.onMouseEnter.bind(this);
      this.onMouseLeave = this.onMouseLeave.bind(this);
      this.onFocusIn = this.onFocusIn.bind(this);
      this.onFocusOut = this.onFocusOut.bind(this);
      this.onBlockSelect = this.onBlockSelect.bind(this);
      this.onSectionSelect = this.onSectionSelect.bind(this);
      this.onKeyDown = this.onKeyDown.bind(this);
      this.onTextUpdateRaf = null;
      this.lastFadeIndex = 0;
      this.progressDirection = "forward";
    }

    connectedCallback() {
      this.sectionId =
        this.closest(".shopify-section")?.dataset?.sectionId || null;
      this.track = this.querySelector("[data-slider-track]");
      this.dots = this.querySelector("[data-slider-dots]");
      this.progress = this.querySelector("[data-slidershow-progress]");
      if (this.progress) {
        this.progressCurrent = this.progress.querySelector(
          "[data-slidershow-progress-current]",
        );
        this.progressTotal = this.progress.querySelector(
          "[data-slidershow-progress-total]",
        );
        this.progressSegments = Array.from(
          this.progress.querySelectorAll("[data-slidershow-progress-segment]"),
        ).map((segment) => ({
          segment,
          fill: segment.querySelector("[data-slidershow-progress-fill]"),
        }));
        this.progressSubtitle = this.progress.querySelector(
          "[data-slidershow-progress-subtitle]",
        );
        this.progressSegments.forEach(({ fill }) => {
          if (!fill) return;
          fill.style.transitionDuration = "0ms";
          fill.style.transitionTimingFunction = "linear";
          fill.style.transformOrigin = "left center";
          fill.style.transform = "scaleX(0)";
          fill.dataset.progressScale = "0";
        });
      }
      if (!this.track) return;
      this.slides = Array.from(this.track.children);
      if (!this.slides.length) return;
      this.setupFadeTrack();
      this.setupDots();
      this.setupHoverNav();
      this.setupA11yNav();
      this.setupAutoplay();
      this.bindEditorEvents();
      this.addEventListener("keydown", this.onKeyDown);
      this.updateTextForIndex(this.fadeIndex);
      this.updateProgress();
    }

    disconnectedCallback() {
      this.teardownHoverNav();
      this.teardownA11yNav();
      if (this.dots) {
        this.dots.removeEventListener("click", this.onDotClick);
      }
      if (this.track) {
        this.track.removeEventListener("pointerdown", this.onFadePointerDown);
      }
      this.removeEventListener("keydown", this.onKeyDown);
      this.stopAutoplay();
      this.removeAutoplayListeners();
      this.unbindEditorEvents();
    }

    bindEditorEvents() {
      if (!window.Shopify || !Shopify.designMode) return;
      document.addEventListener("shopify:block:select", this.onBlockSelect);
      document.addEventListener("shopify:section:select", this.onSectionSelect);
    }

    unbindEditorEvents() {
      if (!window.Shopify || !Shopify.designMode) return;
      document.removeEventListener("shopify:block:select", this.onBlockSelect);
      document.removeEventListener("shopify:section:select", this.onSectionSelect);
    }

    onBlockSelect(event) {
      const blockId = event?.detail?.blockId;
      const sectionId = event?.detail?.sectionId;
      if (this.sectionId && sectionId && this.sectionId !== sectionId) return;
      if (!blockId) return;
      const blockEl =
        this.querySelector(`[data-shopify-editor-block-id="${blockId}"]`) ||
        this.querySelector(`[data-block-id="${blockId}"]`);
      if (!blockEl) return;
      const slide = blockEl.closest(".slidershow__slide");
      if (!slide) return;
      const index = this.slides.indexOf(slide);
      if (index < 0) return;
      this.setFadeIndex(index);
      this.resetAutoplayAfterManual();
    }

    onSectionSelect(event) {
      const sectionId = event?.detail?.sectionId;
      if (this.sectionId && sectionId && this.sectionId !== sectionId) return;
      if (sectionId && this.sectionId === sectionId) {
        this.setFadeIndex(0);
        this.resetAutoplayAfterManual();
      }
    }

    setupFadeTrack() {
      if (!this.track) return;
      this.track.style.overflow = "hidden";
      this.track.style.scrollBehavior = "auto";
      this.track.style.scrollSnapType = "none";
      this.track.style.display = "block";
      this.track.style.position = "relative";
      this.track.classList.remove("snap-x", "snap-mandatory");
      this.track.style.touchAction = "pan-y";
      this.track.addEventListener("pointerdown", this.onFadePointerDown);
      this.slides.forEach((slide, index) => {
        slide.style.position = "absolute";
        slide.style.inset = "0";
        slide.style.opacity = index === 0 ? "1" : "0";
        slide.style.pointerEvents = index === 0 ? "auto" : "none";
        slide.style.transition =
          "opacity 1800ms cubic-bezier(0.22, 0.61, 0.36, 1)";
        slide.style.willChange = "opacity";
        slide.style.transform = "translateZ(0)";
        slide.style.zIndex = index === 0 ? "1" : "0";
        if (index !== 0) slide.inert = true;
      });
      this.fadeIndex = 0;
      this.lastFadeIndex = 0;
    }

    setupDots() {
      if (!this.dots || !this.track) return;
      if (this.slides.length < 2) {
        this.dots.innerHTML = "";
        this.dots.style.display = "none";
        return;
      }
      this.dots.style.display = "";
      const baseClasses = this.dots.getAttribute("data-slider-dot-classes") || "";
      const baseOpacityAttr = this.dots.getAttribute("data-slider-dot-opacity");
      const baseOpacity =
        baseOpacityAttr !== null ? Number.parseFloat(baseOpacityAttr) : 0.3;
      this.dots.innerHTML = "";
      this.slides.forEach((_, index) => {
        const button = document.createElement("button");
        button.type = "button";
        button.setAttribute("data-slider-dot", index);
        const slideLabel = this.getAttribute("data-slide-label") || "Go to slide";
        button.setAttribute("aria-label", `${slideLabel} ${index + 1}`);
        if (baseClasses) {
          button.className = baseClasses;
        }
        if (!Number.isNaN(baseOpacity)) {
          button.style.opacity = String(baseOpacity);
        }
        this.dots.appendChild(button);
      });
      this.dots.addEventListener("click", this.onDotClick);
      this.updateDots();
    }

    updateDots() {
      if (!this.dots) return;
      const dots = Array.from(this.dots.querySelectorAll("[data-slider-dot]"));
      if (!dots.length) return;
      const activeClasses =
        this.dots.getAttribute("data-slider-dot-active-classes") || "";
      const activeClassList = activeClasses
        .split(" ")
        .map((value) => value.trim())
        .filter(Boolean);
      const baseOpacityAttr = this.dots.getAttribute("data-slider-dot-opacity");
      const activeOpacityAttr = this.dots.getAttribute(
        "data-slider-dot-active-opacity",
      );
      const baseOpacity =
        baseOpacityAttr !== null ? Number.parseFloat(baseOpacityAttr) : 0.3;
      const activeOpacity =
        activeOpacityAttr !== null ? Number.parseFloat(activeOpacityAttr) : 1;
      dots.forEach((dot, index) => {
        const isActive = index === this.fadeIndex;
        dot.setAttribute("aria-current", isActive ? "true" : "false");
        if (activeClassList.length) {
          activeClassList.forEach((className) => {
            dot.classList.toggle(className, isActive);
          });
        }
        if (!Number.isNaN(baseOpacity) && !Number.isNaN(activeOpacity)) {
          dot.style.opacity = isActive
            ? String(activeOpacity)
            : String(baseOpacity);
        }
      });
    }

    setFadeIndex(nextIndex) {
      if (!this.slides.length) return;
      const total = this.slides.length;
      let targetIndex = nextIndex;
      if (targetIndex < 0) targetIndex = total - 1;
      if (targetIndex >= total) targetIndex = 0;
      if (targetIndex === this.fadeIndex) return;
      const prevSlide = this.slides[this.fadeIndex];
      const nextSlide = this.slides[targetIndex];
      if (!prevSlide || !nextSlide) return;
      this.progressDirection = this.getProgressDirection(
        this.fadeIndex,
        targetIndex,
        total,
      );
      this.lastFadeIndex = this.fadeIndex;
      requestAnimationFrame(() => {
        prevSlide.style.opacity = "0";
        prevSlide.style.pointerEvents = "none";
        prevSlide.style.zIndex = "0";
        prevSlide.inert = true;
        nextSlide.style.opacity = "1";
        nextSlide.style.pointerEvents = "auto";
        nextSlide.style.zIndex = "1";
        nextSlide.inert = false;
      });
      this.fadeIndex = targetIndex;
      this.updateTextForIndex(this.fadeIndex);
      this.updateDots();
      this.updateProgress();
    }

    getProgressDirection(prevIndex, nextIndex, total) {
      if (total <= 1) return "forward";
      if (prevIndex === total - 1 && nextIndex === 0) return "forward";
      if (prevIndex === 0 && nextIndex === total - 1) return "backward";
      return nextIndex > prevIndex ? "forward" : "backward";
    }

    onPrevClick() {
      this.setFadeIndex(this.fadeIndex - 1);
      this.resetAutoplayAfterManual();
    }

    onNextClick() {
      this.setFadeIndex(this.fadeIndex + 1);
      this.resetAutoplayAfterManual();
    }

    onDotClick(event) {
      const dot = event.target.closest("[data-slider-dot]");
      if (!dot || !this.dots || !this.dots.contains(dot)) return;
      const index = Number.parseInt(dot.getAttribute("data-slider-dot"), 10);
      if (Number.isNaN(index)) return;
      this.setFadeIndex(index);
      this.resetAutoplayAfterManual();
    }

    setupHoverNav() {
      this.hoverNav = this.querySelector("[data-slidershow-hover-nav]");
      if (!this.hoverNav) return;
      this.cursorPrev = this.querySelector("[data-slidershow-cursor='prev']");
      this.cursorNext = this.querySelector("[data-slidershow-cursor='next']");
      this.hoverZones = Array.from(
        this.querySelectorAll("[data-slidershow-zone]"),
      );
      if (!this.hoverZones.length) return;
      this.hoverZones.forEach((zone) => {
        zone.addEventListener("mousemove", this.onZoneMove);
        zone.addEventListener("click", this.onZoneClick);
      });
      this.hoverNav.addEventListener("mouseleave", this.onHoverLeave);
    }

    teardownHoverNav() {
      if (!this.hoverNav || !this.hoverZones.length) return;
      this.enableZones();
      this.hoverZones.forEach((zone) => {
        zone.removeEventListener("mousemove", this.onZoneMove);
        zone.removeEventListener("click", this.onZoneClick);
      });
      this.hoverNav.removeEventListener("mouseleave", this.onHoverLeave);
      this.hoverNav = null;
      this.cursorPrev = null;
      this.cursorNext = null;
      this.hoverZones = [];
    }

    setupA11yNav() {
      this.a11yPrev = this.querySelector("[data-slider-prev-a11y]");
      this.a11yNext = this.querySelector("[data-slider-next-a11y]");
      if (this.a11yPrev) {
        this.a11yPrev.addEventListener("click", this.onPrevClick);
      }
      if (this.a11yNext) {
        this.a11yNext.addEventListener("click", this.onNextClick);
      }
    }

    teardownA11yNav() {
      if (this.a11yPrev) {
        this.a11yPrev.removeEventListener("click", this.onPrevClick);
        this.a11yPrev = null;
      }
      if (this.a11yNext) {
        this.a11yNext.removeEventListener("click", this.onNextClick);
        this.a11yNext = null;
      }
    }

    onKeyDown(event) {
      if (
        event.target &&
        event.target.closest("input, textarea, select, [contenteditable]")
      ) {
        return;
      }
      if (event.key === "ArrowLeft") {
        event.preventDefault();
        this.onPrevClick();
      } else if (event.key === "ArrowRight") {
        event.preventDefault();
        this.onNextClick();
      }
    }

    isOverContent(event) {
      const slide = this.slides[this.fadeIndex];
      if (!slide) return false;
      const textEl = slide.querySelector("[data-slidershow-text]");
      if (!textEl) return false;
      const r = textEl.getBoundingClientRect();
      return (
        event.clientX >= r.left &&
        event.clientX <= r.right &&
        event.clientY >= r.top &&
        event.clientY <= r.bottom
      );
    }

    onZoneMove(event) {
      if (!this.cursorPrev || !this.cursorNext) return;
      if (this.isOverContent(event)) {
        this.onHoverLeave();
        this.disableZones();
        return;
      }
      const zone = event.currentTarget;
      const direction = zone.getAttribute("data-slidershow-zone");
      const rect = this.getBoundingClientRect();
      const nextLeft = event.clientX - rect.left;
      const nextTop = event.clientY - rect.top;
      const showPrev = direction === "prev";
      const activeCursor = showPrev ? this.cursorPrev : this.cursorNext;
      const inactiveCursor = showPrev ? this.cursorNext : this.cursorPrev;
      activeCursor.style.left = `${nextLeft}px`;
      activeCursor.style.top = `${nextTop}px`;
      activeCursor.classList.add("is-visible");
      activeCursor.classList.remove("is-hidden");
      inactiveCursor.classList.remove("is-visible");
      inactiveCursor.classList.add("is-hidden");
    }

    onHoverLeave() {
      if (this.cursorPrev) {
        this.cursorPrev.classList.remove("is-visible");
        this.cursorPrev.classList.add("is-hidden");
      }
      if (this.cursorNext) {
        this.cursorNext.classList.remove("is-visible");
        this.cursorNext.classList.add("is-hidden");
      }
    }

    disableZones() {
      if (this._zonesDisabled) return;
      this._zonesDisabled = true;
      this.hoverZones.forEach((z) => {
        z.style.pointerEvents = "none";
      });
      this.addEventListener("mousemove", this.onRestoreZones);
    }

    enableZones() {
      if (!this._zonesDisabled) return;
      this._zonesDisabled = false;
      this.hoverZones.forEach((z) => {
        z.style.pointerEvents = "";
      });
      this.removeEventListener("mousemove", this.onRestoreZones);
    }

    onRestoreZones(event) {
      if (!this.isOverContent(event)) {
        this.enableZones();
      }
    }

    onZoneClick(event) {
      if (this.isOverContent(event)) return;
      const zone = event.currentTarget;
      const direction = zone.getAttribute("data-slidershow-zone");
      if (direction === "prev") {
        this.onPrevClick();
      } else {
        this.onNextClick();
      }
    }

    resetAutoplayAfterManual() {
      if (!this.autoplayEnabled) return;
      this.stopAutoplay();
      this.updateProgressBar();
      this.startAutoplay();
    }

    onFadePointerDown(event) {
      if (!this.track) return;
      if (event.button !== undefined && event.button !== 0) return;
      const target = event.target;
      if (
        target &&
        target.closest(
          "a[href], button, input, textarea, select, [data-slider-no-drag]",
        )
      ) {
        return;
      }
      this.fadePointerId = event.pointerId;
      this.fadeStartX = event.clientX;
      this.fadeStartY = event.clientY;
      this.fadeDeltaX = 0;
      this.fadeDeltaY = 0;
      this.track.classList.add("select-none");
      this.track.setPointerCapture(event.pointerId);
      this.track.addEventListener("pointermove", this.onFadePointerMove);
      this.track.addEventListener("pointerup", this.onFadePointerUp);
      this.track.addEventListener("pointercancel", this.onFadePointerUp);
    }

    onFadePointerMove(event) {
      if (this.fadePointerId !== event.pointerId) return;
      this.fadeDeltaX = event.clientX - this.fadeStartX;
      this.fadeDeltaY = event.clientY - this.fadeStartY;
    }

    onFadePointerUp(event) {
      if (this.fadePointerId !== event.pointerId) return;
      const absX = Math.abs(this.fadeDeltaX);
      const absY = Math.abs(this.fadeDeltaY);
      const startIndex = this.fadeIndex;
      if (absX > 40 && absX > absY) {
        if (this.fadeDeltaX < 0) {
          this.setFadeIndex(this.fadeIndex + 1);
        } else {
          this.setFadeIndex(this.fadeIndex - 1);
        }
      }
      if (this.fadeIndex !== startIndex) {
        this.resetAutoplayAfterManual();
      }
      this.track.removeEventListener("pointermove", this.onFadePointerMove);
      this.track.removeEventListener("pointerup", this.onFadePointerUp);
      this.track.removeEventListener("pointercancel", this.onFadePointerUp);
      this.track.classList.remove("select-none");
      this.track.releasePointerCapture(event.pointerId);
      this.fadePointerId = null;
    }

    setupAutoplay() {
      const autoplayAttr = this.getAttribute("data-slider-autoplay");
      const intervalAttr = Number.parseInt(
        this.getAttribute("data-slider-interval"),
        10,
      );
      this.autoplayEnabled =
        autoplayAttr !== null &&
        autoplayAttr !== "false" &&
        autoplayAttr !== "0";
      if (!this.autoplayEnabled) return;
      if (!Number.isNaN(intervalAttr) && intervalAttr > 0) {
        this.autoplayInterval = intervalAttr;
      }
      const pauseAttr = this.getAttribute("data-slider-pause-on-hover");
      this.pauseOnHover = pauseAttr !== "false";
      const ignoreHoverAttr = this.getAttribute(
        "data-slidershow-ignore-hover",
      );
      this.ignoreHoverPause = ignoreHoverAttr === "true";
      const ignoreFocusAttr = this.getAttribute(
        "data-slidershow-ignore-focus",
      );
      this.ignoreFocusPause = ignoreFocusAttr === "true";
      const ignoreVisibilityAttr = this.getAttribute(
        "data-slidershow-ignore-visibility",
      );
      this.ignoreVisibility = ignoreVisibilityAttr === "true";
      if (window.matchMedia) {
        this.prefersReducedMotion = window.matchMedia(
          "(prefers-reduced-motion: reduce)",
        );
      }
      this.addAutoplayListeners();
      this.startAutoplay();
      this.updateProgressBarDuration();
      setTimeout(() => {
        this.startAutoplay();
      }, 50);
    }

    addAutoplayListeners() {
      if (!this.ignoreVisibility) {
        document.addEventListener("visibilitychange", this.onVisibilityChange);
      }
      if (this.pauseOnHover && !this.ignoreHoverPause) {
        this.addEventListener("mouseenter", this.onMouseEnter);
        this.addEventListener("mouseleave", this.onMouseLeave);
      }
      if (!this.ignoreFocusPause) {
        this.addEventListener("focusin", this.onFocusIn);
        this.addEventListener("focusout", this.onFocusOut);
      }
    }

    removeAutoplayListeners() {
      if (!this.ignoreVisibility) {
        document.removeEventListener("visibilitychange", this.onVisibilityChange);
      }
      if (this.pauseOnHover) {
        this.removeEventListener("mouseenter", this.onMouseEnter);
        this.removeEventListener("mouseleave", this.onMouseLeave);
      }
      if (!this.ignoreFocusPause) {
        this.removeEventListener("focusin", this.onFocusIn);
        this.removeEventListener("focusout", this.onFocusOut);
      }
    }

    onVisibilityChange() {
      if (document.hidden) {
        this.pauseAutoplay();
      } else {
        this.resumeAutoplay();
      }
    }

    onMouseEnter() {
      this.pauseAutoplay();
    }

    onMouseLeave() {
      this.resumeAutoplay();
    }

    onFocusIn() {
      this.pauseAutoplay();
    }

    onFocusOut() {
      this.resumeAutoplay();
    }

    startAutoplay() {
      if (!this.autoplayEnabled) return;
      if (this.slides.length < 2) return;
      this.autoplayPaused = false;
      if (this.autoplayTimer) {
        clearInterval(this.autoplayTimer);
        this.autoplayTimer = null;
      }
      this.updateProgressBar();
      this.autoplayTimer = setInterval(() => {
        if (this.autoplayPaused) return;
        this.setFadeIndex(this.fadeIndex + 1);
      }, this.autoplayInterval);
    }

    stopAutoplay() {
      if (!this.autoplayTimer) return;
      clearInterval(this.autoplayTimer);
      this.autoplayTimer = null;
    }

    pauseAutoplay() {
      if (!this.autoplayEnabled) return;
      this.autoplayPaused = true;
    }

    resumeAutoplay() {
      if (!this.autoplayEnabled) return;
      this.autoplayPaused = false;
      this.startAutoplay();
    }

    updateProgress() {
      if (!this.progress) return;
      const total = this.slides.length;
      const current = this.fadeIndex + 1;
      if (this.progressCurrent) {
        this.progressCurrent.textContent = String(current).padStart(2, "0");
      }
      if (this.progressTotal) {
        this.progressTotal.textContent = String(total).padStart(2, "0");
      }
      if (this.progressSubtitle) {
        const slide = this.slides[this.fadeIndex];
        const subtitle = slide
          ? slide.getAttribute("data-slidershow-subtitle")
          : "";
        this.progressSubtitle.textContent = subtitle || "";
      }
      this.updateProgressBar();
    }

    updateProgressBarDuration() {
      if (!this.progressSegments.length) return;
      this.progressSegments.forEach(({ fill }) => {
        if (!fill) return;
        fill.style.transitionDuration = `${this.autoplayInterval}ms`;
        fill.style.transitionTimingFunction = "linear";
      });
    }

    updateProgressBar() {
      if (!this.progressSegments.length) return;
      const total = this.slides.length || 1;
      const currentIndex = this.fadeIndex;
      const applyScale = (fill, nextScale, durationMs) => {
        if (!fill) return;
        fill.style.transitionTimingFunction = "linear";
        fill.style.transformOrigin = "left center";
        if (durationMs === 0) {
          fill.style.transitionProperty = "none";
          fill.style.transitionDuration = "0ms";
          fill.style.transform = `scaleX(${nextScale})`;
          requestAnimationFrame(() => {
            fill.style.transitionProperty = "transform";
          });
        } else {
          fill.style.transitionProperty = "transform";
          fill.style.transitionDuration = `${durationMs}ms`;
          fill.style.transform = `scaleX(${nextScale})`;
        }
        fill.dataset.progressScale = String(nextScale);
      };
      if (!this.autoplayEnabled) {
        this.progressSegments.forEach(({ fill }, index) => {
          if (!fill) return;
          const nextScale = index <= currentIndex ? 1 : 0;
          const prevScale = Number.parseFloat(
            fill.dataset.progressScale || "0",
          );
          const shouldAnimate =
            this.progressDirection === "forward"
              ? nextScale > prevScale
              : nextScale < prevScale;
          const durationMs = shouldAnimate ? 300 : 0;
          applyScale(fill, nextScale, durationMs);
        });
        return;
      }
      this.progressSegments.forEach(({ fill }, index) => {
        if (!fill) return;
        const nextScale = index < currentIndex ? 1 : 0;
        const prevScale = Number.parseFloat(
          fill.dataset.progressScale || "0",
        );
        const shouldAnimate =
          this.progressDirection === "forward"
            ? nextScale > prevScale
            : nextScale < prevScale;
        const durationMs = shouldAnimate ? 300 : 0;
        applyScale(fill, nextScale, durationMs);
      });
      requestAnimationFrame(() => {
        const current = this.progressSegments[currentIndex];
        if (!current || !current.fill) return;
        current.fill.style.transitionProperty = "none";
        current.fill.style.transitionDuration = "0ms";
        current.fill.style.transitionTimingFunction = "linear";
        current.fill.style.transformOrigin = "left center";
        current.fill.style.transform = "scaleX(0)";
        current.fill.dataset.progressScale = "0";
        void current.fill.offsetWidth;
        current.fill.style.transitionProperty = "transform";
        current.fill.style.transitionDuration = `${this.autoplayInterval}ms`;
        current.fill.style.transitionTimingFunction = "linear";
        current.fill.style.transform = "scaleX(1)";
        current.fill.dataset.progressScale = "1";
      });
    }

    updateTextForIndex(activeIndex) {
      this.slides.forEach((slide, index) => {
        const text = slide.querySelector("[data-slidershow-text]");
        const mediaNodes = slide.querySelectorAll("[data-slidershow-media]");
        if (text) {
          const isActive = index === activeIndex;
          text.classList.toggle("translate-y-0", isActive);
          text.classList.toggle("opacity-100", isActive);
          text.classList.toggle("translate-y-12", !isActive);
          text.classList.toggle("opacity-0", !isActive);
        }
        mediaNodes.forEach((media) => {
          if (index !== activeIndex) {
            const computed = window.getComputedStyle(media);
            const currentTransform = computed.transform;
            if (currentTransform && currentTransform !== "none") {
              media.style.transform = currentTransform;
            }
            media.classList.remove("is-zooming");
            return;
          }
          media.style.transform = "";
          media.classList.remove("is-zooming");
          void media.offsetWidth;
          media.classList.add("is-zooming");
        });
      });
    }
  }

  customElements.define("slidershow-component", SliderShow);
})();
